// "Paint" — material intensity by make and panel. The refinish story the booth
// and the paint estimator read to plan materials and booth time.

import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts"
import { ChapterSection, ChartCard, BarTip, Highlight } from "../room-chrome"
import { AXIS_TICK, CAT, GRID_STROKE, PAINT_MATERIALS_RATE, RAMP, fmt$ } from "../room-model"
import type { HighlightAction, HighlightFinding, derivePaint } from "../derive"

type Paint = ReturnType<typeof derivePaint>

export function PaintChapter({ paint, highlight, onHighlight, rangeLabel }: {
  paint: Paint
  highlight?: HighlightFinding
  onHighlight: (a: HighlightAction) => void
  rangeLabel: string
}) {
  // Dollar amounts on the bars (hours × materials rate), hours in the tooltip —
  // per the CEO's Jul 14 review ("it needs to be the dollar amount").
  const byMake = paint.byMake.map(r => ({
    make: r.make, value: Math.round(r.avg * PAINT_MATERIALS_RATE),
    tip: `${fmt$(r.avg * PAINT_MATERIALS_RATE)} avg materials (${r.avg.toFixed(1)}h) · ${r.count} est`,
  }))
  const byPanel = paint.byPanel.map(r => ({
    name: r.name, value: Math.round(r.hours * PAINT_MATERIALS_RATE),
    tip: `${fmt$(r.hours * PAINT_MATERIALS_RATE)} materials (${r.hours.toFixed(1)}h refinish) · ${Math.round(r.pct)}% of total`,
  }))
  const empty = paint.total === 0

  return (
    <ChapterSection id="paint" eyebrow="Paint / materials" title="Paint & materials spend" audience="For the booth">
      {empty ? (
        <ChartCard title="Paint materials" sub="Materials $ across the period">
          <p className="h-[180px] grid place-items-center text-sm text-[#8B897E]">No refinish-operation data in this view.</p>
        </ChartCard>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <ChartCard title="Paint materials by make" sub={`Average materials $ per estimate · at $${PAINT_MATERIALS_RATE}/refinish-hour · ${rangeLabel}`}>
            {({ expanded }) => (
              <div style={{ height: Math.max(byMake.length * (expanded ? 34 : 26) + 24, 160) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byMake} layout="vertical" margin={{ left: 16, right: 24 }}>
                    <CartesianGrid horizontal={false} stroke={GRID_STROKE} />
                    <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                    <YAxis type="category" dataKey="make" tick={AXIS_TICK} tickLine={false} axisLine={false} width={80} />
                    <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
                      {byMake.map((r, i) => <Cell key={r.make} fill={i === 0 ? CAT.deep : CAT.brand} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </ChartCard>

          <ChartCard title="Paint materials by panel" sub="Materials $ by body panel — where the booth spend goes">
            {({ expanded }) => (
              <>
              {highlight && <Highlight text={highlight.text} onClick={highlight.action ? () => onHighlight(highlight.action!) : undefined} />}
              <div style={{ height: Math.max(byPanel.length * (expanded ? 34 : 26) + 24, 160) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byPanel} layout="vertical" margin={{ left: 16, right: 24 }}>
                    <CartesianGrid horizontal={false} stroke={GRID_STROKE} />
                    <XAxis type="number" tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={v => `$${(v / 1000).toFixed(v >= 1000 ? 1 : 0)}${v >= 1000 ? "k" : ""}`} />
                    <YAxis type="category" dataKey="name" tick={AXIS_TICK} tickLine={false} axisLine={false} width={110} />
                    <Tooltip content={<BarTip />} cursor={{ fill: "rgba(0,0,0,0.03)" }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={20}>
                      {byPanel.map((r, i) => <Cell key={r.name} fill={i < 3 ? CAT.brand : RAMP[0]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              </>
            )}
          </ChartCard>
        </div>
      )}
    </ChapterSection>
  )
}
